/**
 * O avisador — parte D1 (01/10/2026), spec §6.1. Fora do React e testável sem componente.
 *
 * O servidor decide o nível de cada pessoa (`niveis_para`) e o manda no evento como
 * `aviso`: `som` → aviso na tela + som; `aviso` → só o aviso; `sino` → nada na tela (só o
 * contador). Evento sem `aviso` (backend antigo no meio do deploy, produtor que ainda não
 * decide nível): a regra de antes da D1 — warning/critical/error aparecem, sem som; info não.
 */
import { tocarSomDeAviso, type TipoDeSom } from "@/lib/somDeAviso";
import { reservarJanelaDoSom } from "./janelaDoSom";
import { somDoAparelhoLigado } from "./somDoAparelho";
import { contarAvisoNoTitulo } from "./tituloDaAba";
import { estaAbaToca } from "./travaDoSom";

export interface EventoDoAviso {
  aviso?: string | null;
  severity?: string | null;
}

export interface DecisaoDoAviso {
  mostrar: boolean;
  tocar: boolean;
  som: TipoDeSom;
}

const GRAVES = new Set(["critical", "error"]);

export function decidirAviso(evento: EventoDoAviso, somLigado: boolean): DecisaoDoAviso {
  const som: TipoDeSom = GRAVES.has(evento.severity ?? "") ? "critico" : "aviso";
  switch (evento.aviso) {
    case "som":
      return { mostrar: true, tocar: somLigado, som };
    case "aviso":
      return { mostrar: true, tocar: false, som };
    case "sino":
      return { mostrar: false, tocar: false, som };
    default:
      return {
        mostrar: evento.severity === "warning" || GRAVES.has(evento.severity ?? ""),
        tocar: false,
        som,
      };
  }
}

/** Decide, mostra (pela função recebida), conta no título e toca — nesta ordem. */
export function anunciar(evento: EventoDoAviso, mostrar: () => void): DecisaoDoAviso {
  const decisao = decidirAviso(evento, somDoAparelhoLigado());
  if (decisao.mostrar) {
    try {
      mostrar();
      contarAvisoNoTitulo();
    } catch (erro) {
      // O aviso na tela que falha não cala o resto (o sino já contou).
      console.warn("[avisos] o aviso na tela falhou:", erro);
    }
  }
  // ⚠️ A ordem importa: a aba que não toca não pode gastar a janela de 5 s das outras.
  if (decisao.tocar && estaAbaToca() && reservarJanelaDoSom()) {
    tocarSomDeAviso(decisao.som);
  }
  return decisao;
}
